import { type ScoreboardResponse } from '@on3/api';
import { useAuth } from '@on3/ui-lib/index';
import { userHasPermission } from '@on3/ui-lib/utils/auth/access';
import { gameScorer } from '@on3/ui-lib/utils/auth/constants';
import { Edit } from '@public/hocs/Game/Edit/Edit';
import { createContext, useEffect, useMemo, useReducer, useRef } from 'react';
import { createPortal } from 'react-dom';

type GameType = ScoreboardResponse & {
  currentTeam: { key: number; name: string; primaryColor: string };
};

export type GameActions = {
  edit?: () => void;
};

type GameActionStates = {
  editing: boolean;
};

const initialState = {
  editing: false,
};

const reducer = (state: GameActionStates, action: any) => {
  switch (action.type) {
    case 'edit':
      return { ...state, editing: true };
    case 'close':
      return initialState;
    default:
      return state;
  }
};

export const GameWrapperContext = createContext({
  game: {} as GameType,
  close: () => {},
});

export const GameWrapper = ({
  game,
  children,
}: {
  game: GameType;
  children: (gameActions: GameActions) => JSX.Element;
}) => {
  const portal = useRef<HTMLElement | null>(null);
  const { user } = useAuth();
  const [state, dispatch] = useReducer(reducer, initialState);
  const canEdit = userHasPermission(gameScorer, user);

  useEffect(() => {
    if (document) {
      portal.current = document.getElementById('portal-root');
    }
  }, []);

  const close = () => {
    dispatch({ type: 'close' });
  };

  const edit = () => {
    dispatch({ type: 'edit' });
  };

  const gameActions = useMemo(() => {
    const actions = {} as GameActions;

    if (canEdit) actions.edit = edit;

    return actions;
  }, [canEdit]);

  return (
    <GameWrapperContext.Provider value={{ game, close }}>
      {children(gameActions)}
      {portal.current && (
        <>{state.editing && createPortal(<Edit />, portal.current)}</>
      )}
    </GameWrapperContext.Provider>
  );
};
